import type { ParseKeys, TFunction } from 'i18next';
import type {
  ChtOverrides,
  DesignPointStatus,
  SimpleAlgorithmOverrides,
  SimRetryDecisionSummary,
  SimulationStatus,
  StopLimit,
  StudyStatus,
} from '@/api/generated/types.gen';
import type {
  FlowAxis,
  FlowType,
  FluidDerived,
  SolverSettings,
  StructuralStaticSolver,
  SweepValue,
  ThermalFeaSolver,
} from './types';
import {
  DEFAULT_SOLVER_CONFIG,
  isCfdFlowSolver,
  isList,
  isStructuralStaticSolver,
  isThermalFeaSolver,
} from './types';
import {
  readStructuralSeed,
  pruneStructuralParameterSweeps,
  structuralVaryingPaths,
} from './structuralSweepAxes';
import {
  readThermalSeed,
  pruneThermalParameterSweeps,
  thermalVaryingPaths,
} from './thermalSweepAxes';
import {
  structuralSweepPathLabel,
  structuralSweepPathShortLabel,
} from './structuralSweepPathLabel';
import { statusInProgress, statusTone, toneDotClass } from '@/lib/statusTone';
import { secondsToHours, TIME_LIMIT_DEFAULT_S } from './time-limit';

export const ACTIVE_STUDY_STATUSES: ReadonlySet<StudyStatus> = new Set([
  'running',
  'preprocessing',
]);

/**
 * Results view base modes (ENG-1842) — the three tabs in the header control:
 * Overview (the report, statistics tile included), Design Points (master
 * table + selection strip + detail) and Compare (two runs side by side).
 *
 * Each is a ROUTE, at `/simops/$studyId/results/<mode>`. They were `?mode=`
 * on one route for a release; they are paths now because they are pages, and
 * a page is identified by its path — a comparison somebody sends to a
 * colleague should read as one in the address bar. The enum stays here rather
 * than in the route modules so `-components/**` can name a mode without
 * importing from a route (ENG-1190).
 *
 * `stats` used to be a fourth mode and a sub-view of the Design Points card;
 * the statistics are an Overview tile now, so the mode is gone.
 */
export const ANALYZE_MODES = ['overview', 'points', 'compare'] as const;
export type AnalyzeMode = (typeof ANALYZE_MODES)[number];

/**
 * What the Analyze header's segmented control can highlight. Every base mode
 * has a tab, so this is `AnalyzeMode` itself — only the advanced modes
 * collapse. Kept as a name because the header, its return type and its casts
 * all want to say "a mode with a tab" rather than "a mode".
 */
export type SegmentedMode = AnalyzeMode;

/**
 * Advanced Analyze modes (ENG-1843) — Pareto trade-offs + surrogate evidence.
 * The route zod schema accepts these unconditionally so URL degradation is
 * meaningful (a shared link keeps its shape), and
 * `isSimopsAnalyzeAdvancedModesEnabled()` gates whether the workbench actually
 * renders them at dispatch time. Off (default) collapses `tradeoffs`/`evidence`
 * back to Overview.
 */
export const ADVANCED_ANALYZE_MODES = ['tradeoffs', 'evidence'] as const;
export type AdvancedAnalyzeMode = (typeof ADVANCED_ANALYZE_MODES)[number];
export type AnyAnalyzeMode = AnalyzeMode | AdvancedAnalyzeMode;
export const ALL_ANALYZE_MODES = [
  ...ANALYZE_MODES,
  ...ADVANCED_ANALYZE_MODES,
] as const;

/**
 * The product library a study detail route is being read under.
 *
 * The same study detail tree is mounted twice — under `/simops/$studyId` and
 * under `/evaltool/$studyId` — so that a link to a study's results reads as
 * the product the reader is in. Which one is live is a property of the PATH,
 * not of a search param: `?origin=evaltool` used to carry it, and a reader who
 * dropped the query string (or a link a colleague retyped) landed back in
 * SimOps chrome on a URL that still said EvalTool nowhere.
 */
export const STUDY_LIBRARIES = ['simops', 'evaltool'] as const;
export type StudyLibrary = (typeof STUDY_LIBRARIES)[number];

/**
 * Which library owns this pathname. Anything that is not under `/evaltool` is
 * SimOps, which is also the right answer for the surfaces that mount the
 * workbench from outside either tree (`/share/study/...`): they carry SimOps
 * chrome today.
 */
export function studyLibraryFromPath(pathname: string): StudyLibrary {
  return pathname.startsWith('/evaltool') ? 'evaltool' : 'simops';
}

/**
 * The route each mode is addressed by, per library.
 *
 * A table rather than a template literal, because a `to` built by
 * interpolation is just a string to the router's type checker: a mode renamed
 * on one side of it then fails at runtime as a dead link instead of at `tsc`.
 * Every entry here is a real route file under `$studyId/results/`.
 *
 * Two rows, because the mode switch has to stay inside the library the reader
 * is in — a tab click from `/evaltool/<id>/results/overview` that landed on
 * `/simops/...` would silently move them to another product mid-read.
 */
export const ANALYZE_MODE_ROUTES = {
  simops: {
    overview: '/simops/$studyId/results/overview',
    points: '/simops/$studyId/results/points',
    compare: '/simops/$studyId/results/compare',
    tradeoffs: '/simops/$studyId/results/tradeoffs',
    evidence: '/simops/$studyId/results/evidence',
  },
  evaltool: {
    overview: '/evaltool/$studyId/results/overview',
    points: '/evaltool/$studyId/results/points',
    compare: '/evaltool/$studyId/results/compare',
    tradeoffs: '/evaltool/$studyId/results/tradeoffs',
    evidence: '/evaltool/$studyId/results/evidence',
  },
} as const;

/** Each library's own landing — where its back-link and its not-found card go. */
export const LIBRARY_HOME_ROUTES = {
  simops: '/simops',
  evaltool: '/evaltool',
} as const;

/** The study detail root each library addresses a study by. */
export const STUDY_DETAIL_ROUTES = {
  simops: '/simops/$studyId',
  evaltool: '/evaltool/$studyId',
} as const;

/** The Results step's own route (which redirects to `overview`), per library. */
export const STUDY_RESULTS_ROUTES = {
  simops: '/simops/$studyId/results',
  evaltool: '/evaltool/$studyId/results',
} as const;

/**
 * Which mode a pathname is showing, or `undefined` off the Results step.
 *
 * Read from the path rather than from a route id, because more than one route
 * mounts the workbench and `-components/**` may not pin a `from:` (ENG-1190).
 * The bare `/results` never reaches here — it redirects to `/results/overview`
 * before anything renders.
 */
export function analyzeModeFromPath(
  pathname: string,
): AnyAnalyzeMode | undefined {
  const mode = /\/results\/([^/?#]+)/.exec(pathname)?.[1];
  return isAnyAnalyzeMode(mode) ? mode : undefined;
}

/**
 * Analyze mode fallback for a surface showing the workbench WITHOUT being on
 * a `results/<mode>` route — the bare `/simops/$studyId` of a completed study
 * (ENG-1917). A live `?dp=` selection lands the reader on Design Points so
 * the detail pane paints; otherwise Overview is the landing surface.
 */
export function resolveAnalyzeMode(
  mode: AnyAnalyzeMode | undefined,
  selectedDpId: string | undefined,
): AnyAnalyzeMode {
  return mode ?? (selectedDpId ? 'points' : 'overview');
}

/**
 * The navigation that moves the reader to one Results view (ENG-2640).
 *
 * The rule `useSetAnalyzeMode` applies, as a plain function, because the
 * assistant's `set_simops_view` makes the same move from outside React and a
 * second spelling of it is how the two would drift. Compare has no selection
 * concept, and a `?dp=` left behind there sends the next bare-mode link back
 * to Points; Overview and Points both keep it, so Back restores the detail.
 */
export function analyzeModeNavigation(
  library: StudyLibrary,
  studyId: string,
  next: AnyAnalyzeMode,
) {
  return {
    to: ANALYZE_MODE_ROUTES[library][next],
    params: { studyId },
    search: <T extends object>(prev: T) => ({
      ...prev,
      dp: next === 'compare' ? undefined : (prev as { dp?: string }).dp,
    }),
  };
}

/**
 * Coerce any resolved analyze mode to the value the header's segmented
 * control can highlight. Advanced modes (`tradeoffs`/`evidence`) have no
 * dedicated tab today, so they collapse onto Overview — the button the
 * reader would use to leave the advanced view.
 */
export function segmentedFromMode(mode: AnyAnalyzeMode): SegmentedMode {
  return (ANALYZE_MODES as readonly string[]).includes(mode)
    ? (mode as SegmentedMode)
    : 'overview';
}

/** Type guard covering the full analyze-mode union (base + advanced). */
export function isAnyAnalyzeMode(value: unknown): value is AnyAnalyzeMode {
  return (
    typeof value === 'string' &&
    (ALL_ANALYZE_MODES as readonly string[]).includes(value)
  );
}

/**
 * Cap on `studies.report_body`, mirrored from the backend's
 * `MAX_REPORT_BODY_CHARS`. Doubled from the 32 000 the two prose columns each
 * carried: one body now holds what both held plus five more sections.
 *
 * A write beyond it is rejected with 422 by pydantic, so the editor surfaces
 * a `{{current}} / {{max}}` counter and truncates on paste rather than
 * letting a save fail.
 */
export const STUDY_REPORT_BODY_MAX_CHARS = 64000;
/**
 * Max character count BE `CreateCommentRequest.body` accepts. A write
 * beyond this is rejected with 422 by pydantic — the comment composer
 * surfaces this as a `{{current}} / {{max}}` counter. Sourced from the
 * openapi spec (`maxLength: 10001`); update here if BE changes the cap.
 */
export const COMMENT_BODY_MAX_CHARS = 10001;

export const AWAITING_REVIEW_STATUSES: ReadonlySet<StudyStatus> = new Set([
  'awaiting_bc_review',
  'awaiting_mobo_review',
]);

// A study whose runs are done, in either of the two shapes the backend
// reports. The results surface is the one thing that reads them as a pair —
// `/results` renders the workbench for these and a placeholder for the rest,
// and EvalTool's landing opens on them — so the set lives here rather than
// being respelled at each site.
const COMPLETED_STUDY_STATUSES: ReadonlySet<StudyStatus> = new Set([
  'completed',
  'completed_with_issues',
]);

export function isStudyCompleted(status: StudyStatus): boolean {
  return COMPLETED_STUDY_STATUSES.has(status);
}

/**
 * What EvalTool is FOR: finished work, plus a smart search whose results are
 * waiting on a human decision — `ResultsLayout` renders the workbench for the
 * first two and `ReviewView` for the third, so all three have a surface under
 * `/evaltool`. Everything still meshing or simulating belongs to SimOps.
 *
 * One predicate, several consumers that must not drift: the `/evaltool/$studyId`
 * loader forwards anything outside it to SimOps, and the EvalTool library's rows
 * and cards open what is inside it on the results surface. They disagreed once —
 * the library sent a review-pending study to SimOps while the route admitted it.
 *
 * Written over `isStudyCompleted` rather than as a set of its own, so "finished
 * work PLUS a pending decision" is the code and not just the sentence above it.
 */
export function isEvalToolStudy(status: StudyStatus): boolean {
  return isStudyCompleted(status) || status === 'awaiting_mobo_review';
}

export const TERMINAL_DESIGN_POINT_STATUSES: ReadonlySet<DesignPointStatus> =
  new Set(['completed', 'failed', 'cancelled']);

/**
 * A run that stopped at its iteration or time limit with a checkpoint, so it
 * can be continued (ENG-2864). The backend is renaming the status from
 * `paused` to `stopped` (ENG-2947) and this bundle may meet either, so every
 * check reads this set rather than naming a value. The backend's
 * `STOPPED_SIM_STATUSES`.
 */
export const STOPPED_SIM_STATUSES: ReadonlySet<SimulationStatus> = new Set([
  'paused',
  'stopped',
]);

/** Whether a run stopped at its limit — see `STOPPED_SIM_STATUSES`. */
export function isStoppedRun(
  status: SimulationStatus | null | undefined,
): boolean {
  return status != null && STOPPED_SIM_STATUSES.has(status);
}

/**
 * Whether a run in this state is worth ASKING about continuing — the client
 * half of the backend's `status_admits_continue`.
 *
 * A stopped run is the usual case. A failed one qualifies too when the
 * infrastructure killed it rather than the solver breaking — a Spot reclaim,
 * an OOM, an S3 stall (ENG-2970). So does a completed one, when it is a steady
 * run that ended at its iteration limit: that run converged by the flat rule
 * (it did not diverge, ENG-3042) and can still be carried on for more
 * iterations. Those distinctions are drawn from the failure's cause and the
 * run's ending, which the points list does not carry, so this deliberately
 * answers the weaker question: the authority is `continuable` on the
 * simulation detail, and this only decides whether to go and fetch it.
 *
 * Kept separate from `isStoppedRun`, which several places use to mean a run
 * genuinely stopped at a limit — labels and outcome wording that a failed run
 * must not pick up.
 */
export function mayBeContinuable(
  status: SimulationStatus | null | undefined,
): boolean {
  return isStoppedRun(status) || status === 'failed' || status === 'completed';
}

/**
 * Which limit a stopped run reached: `stop_limit`, falling back to
 * `pause_reason` on a backend that does not send it yet (ENG-2947).
 */
export function stopLimitOf(
  run:
    | {
        stop_limit?: StopLimit | null;
        pause_reason?: StopLimit | null;
      }
    | null
    | undefined,
): StopLimit | null {
  return run?.stop_limit ?? run?.pause_reason ?? null;
}

/**
 * Runs whose output is a result (ENG-2864): a completed run, and a stopped
 * one — it stopped at its own limit with its newest step's numbers. The
 * backend's `RESULT_SIM_STATUSES`.
 */
export const SIM_HAS_RESULTS: ReadonlySet<SimulationStatus> = new Set([
  'completed',
  ...STOPPED_SIM_STATUSES,
]);

/**
 * Status dot for one solver attempt (ENG-1744).
 *
 * Was a `Record<SimulationStatus, string>` that rendered `running` as a static
 * `bg-info`; in-flight states now pulse like every other surface's do.
 */
export function simulationStatusDotClass(status: SimulationStatus): string {
  // `paused` is the old spelling of `stopped` (ENG-2947), and takes its tone.
  const resolved = isStoppedRun(status) ? 'stopped' : status;
  return toneDotClass(statusTone('simulation', resolved), {
    pulse: statusInProgress('simulation', resolved),
  });
}

/**
 * Status dot for a design point (ENG-1744).
 *
 * Was a `Record<DesignPointStatus, string>` carrying its own five-step opacity
 * ladder — `bg-info/60` for `preprocessing` and `mesh_ready`, `/80` for
 * `faces_done`, `bg-muted-foreground/50` for `pending` and `/40` for
 * `cancelled` — none of it documented, and `mesh_ready` blue where the DP
 * meshing icon rendered it amber. Tones now, at full opacity: the progress
 * *stage* is carried by the label beside the dot, which is where a distinction
 * a user must read belongs.
 */
export function designPointStatusDotClass(status: DesignPointStatus): string {
  return toneDotClass(statusTone('designPoint', status), {
    pulse: statusInProgress('designPoint', status),
  });
}

/**
 * `DesignPointStatus` → i18n key, for the status-filter list. Previously the
 * raw wire enum with underscores swapped for spaces and a CSS `capitalize`.
 */
export const DP_STATUS_LABEL_KEY = {
  pending: 'simopsDesignPoints.dpStatus.pending',
  preprocessing: 'simopsDesignPoints.dpStatus.preprocessing',
  meshing: 'simopsDesignPoints.dpStatus.meshing',
  mesh_ready: 'simopsDesignPoints.dpStatus.meshReady',
  faces_done: 'simopsDesignPoints.dpStatus.facesDone',
  simulating: 'simopsDesignPoints.dpStatus.simulating',
  extracting: 'simopsDesignPoints.dpStatus.extracting',
  pending_review: 'simopsDesignPoints.dpStatus.pendingReview',
  completed: 'simopsDesignPoints.dpStatus.completed',
  failed: 'simopsDesignPoints.dpStatus.failed',
  cancelled: 'simopsDesignPoints.dpStatus.cancelled',
} as const satisfies Record<DesignPointStatus, ParseKeys>;

/**
 * `SimulationStatus` → i18n key, for the attempt log, its rows and the detail
 * header's run picker.
 *
 * Deliberately points at keys that already exist rather than adding a sixth
 * group spelling "Completed": the same word rendered from two key groups drifts
 * apart in one locale and not another. A run that has not started reads
 * "Queued" whichever of the two it is, and a finished one "Completed" (not the
 * list filter's "Finished runs").
 */
export const SIMULATION_STATUS_LABEL_KEY = {
  pending: 'simopsDesignPoints.phaseLabel.queued',
  queued: 'simopsDesignPoints.phaseLabel.queued',
  running: 'simopsMisc.statusBadge.running',
  completed: 'simopsDesignPoints.detail.outcomeCompleted',
  failed: 'simopsDesignPoints.dpStatus.failed',
  cancelled: 'simopsDesignPoints.dpStatus.cancelled',
  paused: 'simopsDesignPoints.phaseLabel.stopped',
  stopped: 'simopsDesignPoints.phaseLabel.stopped',
} as const satisfies Record<SimulationStatus, ParseKeys>;

/** A wire enum's label, or the raw value if this bundle has no key for it.
 *
 *  The `satisfies Record<Enum, ParseKeys>` maps callers pass here are
 *  exhaustive at regen time, but FE and BE deploy separately — the backend can
 *  ship a tenth `EscalationReason` before this bundle redeploys. Returning the
 *  raw value fails visibly; `t()` on a nullish key resolves to '', which
 *  renders a label followed by silence. */
export function enumLabel<K extends string>(
  t: TFunction,
  map: Partial<Record<K, ParseKeys>>,
  value: K,
): string {
  const key = map[value];
  return key ? (t(key) as string) : value;
}

/** Sim retry verdicts, lifecycles, confidences and decline reasons → i18n keys
 *  (ENG-1830b).
 *
 *  Same `as const satisfies Record<Enum, ParseKeys>` shape as
 *  `SIMULATION_STATUS_LABEL_KEY` above: a value the backend adds becomes a
 *  compile error here rather than a raw `cap_exhausted` reaching a ja/zh-TW
 *  screenshot (ENG-1744). Note that only holds at regen time — see `enumLabel`
 *  above for what a deploy-skewed unknown value renders. */
export const SIM_DECISION_VERDICT_LABEL_KEY = {
  retry: 'simopsDesignPoints.decisions.verdict.retry',
  escalate: 'simopsDesignPoints.decisions.verdict.escalate',
  decline: 'simopsDesignPoints.decisions.verdict.decline',
} as const satisfies Record<SimRetryDecisionSummary['verdict'], ParseKeys>;

export const SIM_DECISION_LIFECYCLE_LABEL_KEY = {
  proposed: 'simopsDesignPoints.decisions.lifecycle.proposed',
  dispatched: 'simopsDesignPoints.decisions.lifecycle.dispatched',
  dismissed: 'simopsDesignPoints.decisions.lifecycle.dismissed',
  approved: 'simopsDesignPoints.decisions.lifecycle.approved',
} as const satisfies Record<SimRetryDecisionSummary['lifecycle'], ParseKeys>;

export const SIM_DECISION_CONFIDENCE_LABEL_KEY = {
  low: 'simopsDesignPoints.decisions.confidence.low',
  medium: 'simopsDesignPoints.decisions.confidence.medium',
  high: 'simopsDesignPoints.decisions.confidence.high',
} as const satisfies Record<
  NonNullable<SimRetryDecisionSummary['confidence']>,
  ParseKeys
>;

export const SIM_DECISION_REASON_LABEL_KEY = {
  semantic_failure: 'simopsDesignPoints.decisions.reason.semanticFailure',
  advisor_declined: 'simopsDesignPoints.decisions.reason.advisorDeclined',
  low_confidence: 'simopsDesignPoints.decisions.reason.lowConfidence',
  advisor_invalid: 'simopsDesignPoints.decisions.reason.advisorInvalid',
  advisor_timeout: 'simopsDesignPoints.decisions.reason.advisorTimeout',
  validation_failed: 'simopsDesignPoints.decisions.reason.validationFailed',
  previously_tried: 'simopsDesignPoints.decisions.reason.previouslyTried',
  cap_exhausted: 'simopsDesignPoints.decisions.reason.capExhausted',
  gci_ineligible: 'simopsDesignPoints.decisions.reason.gciIneligible',
  advisor_not_entitled:
    'simopsDesignPoints.decisions.reason.advisorNotEntitled',
  engineer_rerun: 'simopsDesignPoints.decisions.reason.engineerRerun',
} as const satisfies Record<
  NonNullable<SimRetryDecisionSummary['reason']>,
  ParseKeys
>;

export interface SweepableField {
  path: string;
  get: (s: SolverSettings) => SweepValue;
}

/** rho, mu, nu are linked — only the two non-derived fields are independently sweepable. */
const FLUID_FIELDS: Record<FluidDerived, SweepableField> = {
  rho: {
    path: 'rho',
    get: (s) => (isCfdFlowSolver(s) ? s.rho : null),
  },
  mu: {
    path: 'mu',
    get: (s) => (isCfdFlowSolver(s) ? s.mu : null),
  },
  nu: {
    path: 'nu',
    get: (s) => (isCfdFlowSolver(s) ? s.nu : null),
  },
};

const BC_SWEEPABLE_FIELDS: readonly SweepableField[] = [
  {
    path: 'inlet.velocity',
    get: (s) => (isCfdFlowSolver(s) ? (s.inlet?.velocity ?? null) : null),
  },
  {
    path: 'outlet.pressure',
    get: (s) => (isCfdFlowSolver(s) ? (s.outlet?.pressure ?? null) : null),
  },
];

function structuralSweepableFields(
  solver: StructuralStaticSolver,
): SweepableField[] {
  return structuralVaryingPaths(solver).map((path) => ({
    path,
    get: (s: SolverSettings): SweepValue => {
      if (!isStructuralStaticSolver(s)) return null;
      const authored = s.parameter_sweeps?.[path];
      if (authored !== undefined) return authored;
      const seed = readStructuralSeed(s, path);
      return seed === null ? null : [seed];
    },
  }));
}

function thermalSweepableFields(solver: ThermalFeaSolver): SweepableField[] {
  return thermalVaryingPaths(solver).map((path) => ({
    path,
    get: (s: SolverSettings): SweepValue => {
      if (!isThermalFeaSolver(s)) return null;
      const authored = s.parameter_sweeps?.[path];
      if (authored !== undefined) return authored;
      const seed = readThermalSeed(s, path);
      return seed === null ? null : [seed];
    },
  }));
}

/**
 * i18n keys for sweep-axis paths shown in column headers, override chips,
 * and chart legends. Raw paths (`inlet.velocity`, `outlet.pressure`, `rho`
 * / `mu` / `nu`) leak the wire schema — users read them as jargon. Prefer
 * this map for any user-facing display; keep the raw path only inside the
 * `setSweepField` switch and other wire-facing code.
 */
export const SWEEP_PATH_LABEL_KEYS: Record<string, ParseKeys> = {
  'inlet.velocity': 'simopsSolver.sweepPaths.inletVelocity',
  'outlet.pressure': 'simopsSolver.sweepPaths.outletPressure',
  rho: 'simopsSolver.sweepPaths.rho',
  mu: 'simopsSolver.sweepPaths.mu',
  nu: 'simopsSolver.sweepPaths.nu',
  initial_temperature_K: 'simopsSolver.sweepPaths.initialTemperature',
};

/** Localized label for a sweep-axis path. Falls back to the raw path
 *  when no key is registered (a future axis added upstream won't blank).
 *  When `t` is absent, returns the raw path so pure-data call sites keep
 *  working. FEA instance-keyed paths use
 *  {@link structuralSweepPathLabel} (ENG-2602). */
export function sweepPathLabel(path: string, t?: TFunction): string {
  const key = SWEEP_PATH_LABEL_KEYS[path];
  if (key && t) return t(key);
  if (t) {
    const structural = structuralSweepPathLabel(path, t);
    if (structural) return structural;
  }
  return path;
}

/**
 * Compact Greek/symbol label for a sweep-axis path — used in chart
 * titles + tick tooltips where "動態黏度 (μ) (Pa·s)" would double-paren
 * and blow out the narrow chart cell. Reads cleanly next to a separate
 * unit suffix rendered by the chart component.
 */
export const SWEEP_PATH_SHORT_LABEL: Record<string, string> = {
  'inlet.velocity': 'V',
  'outlet.pressure': 'p',
  rho: 'ρ',
  mu: 'μ',
  nu: 'ν',
};

/** Compact chart-safe label for a sweep-axis path. Locale-invariant —
 *  the short symbols (`ρ` / `μ` / `ν` / `V` / `p`) render the same in
 *  every locale, so no `t` argument. Falls back to the raw path for
 *  unregistered axes. */
export function sweepPathShortLabel(path: string): string {
  return (
    SWEEP_PATH_SHORT_LABEL[path] ?? structuralSweepPathShortLabel(path) ?? path
  );
}

/**
 * Packed chart-color index per discrete multi-value sweep path — same
 * ordering as CFD `SolverSettingsForm` and `FeaSweepAxes`, so the BC
 * panel seed fields and the DOE axes panel share colors (ENG-2602).
 * RangeSpec axes are skipped so the sequence stays packed.
 */
export function discreteSweepIndexByPath(
  solver: SolverSettings,
): Record<string, number> {
  const out: Record<string, number> = {};
  let next = 0;
  for (const field of getSweepableFields(solver)) {
    const v = field.get(solver);
    out[field.path] = isList(v) && v.length > 1 ? next++ : -1;
  }
  return out;
}

/**
 * Sweepable fields for a given config. Excludes the derived fluid property,
 * since it's recomputed from the two independent ones.
 *
 * CFD: field-embedded axes on the flow leaf.
 * Structural static (ENG-2587): per-instance paths from contacts / bolts /
 * loads, values live in ``parameter_sweeps`` (seed scalars stay untouched).
 * Thermal FEA (ENG-2606 / ENG-3002): lone-face BC paths (+ IC on transient),
 * same ``parameter_sweeps`` shape. CHT / modal: empty in v1.
 */
export function getSweepableFields(
  solver: SolverSettings,
): readonly SweepableField[] {
  if (isCfdFlowSolver(solver)) {
    const primaries = (Object.keys(FLUID_FIELDS) as FluidDerived[]).filter(
      (k) => k !== solver.fluid_derived,
    );
    return [...primaries.map((k) => FLUID_FIELDS[k]), ...BC_SWEEPABLE_FIELDS];
  }
  if (isStructuralStaticSolver(solver)) {
    return structuralSweepableFields(solver);
  }
  if (isThermalFeaSolver(solver)) {
    return thermalSweepableFields(solver);
  }
  return [];
}

/**
 * Write one sweep axis. Generic over `SolverSettings` so CFD leaves keep
 * their template (`internal_flow` stays `internal_flow`) and structural
 * returns a required `template: 'structural_static'`.
 */
export function setSweepField<T extends SolverSettings>(
  solver: T,
  path: string,
  value: SweepValue,
): T {
  if (isStructuralStaticSolver(solver)) {
    const next = { ...(solver.parameter_sweeps ?? {}) };
    if (value === null) {
      delete next[path];
    } else {
      next[path] = value;
    }
    // Drop keys that no longer resolve on this case (contact/bolt/load
    // renamed or removed while a sweep was still authored).
    return pruneStructuralParameterSweeps({
      ...solver,
      parameter_sweeps: next,
    }) as T;
  }
  if (isThermalFeaSolver(solver)) {
    const next = { ...(solver.parameter_sweeps ?? {}) };
    if (value === null) {
      delete next[path];
    } else {
      next[path] = value;
    }
    return pruneThermalParameterSweeps({
      ...solver,
      parameter_sweeps: next,
    }) as T;
  }
  if (!isCfdFlowSolver(solver)) return solver;
  switch (path) {
    case 'rho':
      // Fluid props stay discrete — Range mode would break the rho ↔ mu ↔ nu
      // linkage. Drop a stray RangeSpec and fall back to the default.
      return {
        ...solver,
        rho: isList(value) ? value : DEFAULT_SOLVER_CONFIG.rho,
      };
    case 'mu':
      return {
        ...solver,
        mu: isList(value) ? value : DEFAULT_SOLVER_CONFIG.mu,
      };
    case 'nu':
      return {
        ...solver,
        nu: isList(value) ? value : DEFAULT_SOLVER_CONFIG.nu,
      };
    case 'inlet.velocity':
      return (
        value
          ? { ...solver, inlet: { velocity: value } }
          : { ...solver, inlet: null }
      ) as T;
    case 'outlet.pressure':
      return (
        value
          ? { ...solver, outlet: { pressure: value } }
          : { ...solver, outlet: null }
      ) as T;
    default:
      return solver;
  }
}

export const FLOW_TYPE_LABELS: Record<FlowType, string> = {
  laminar: 'Laminar',
  k_epsilon: 'k-ε (k-epsilon)',
  k_omega: 'k-ω (k-omega)',
  k_omega_sst: 'k-ω SST',
};

/** i18n keys for the flow-type dropdown labels. Non-EN locales prepend a
 *  short natural-language noun to the technical term ("層流 (Laminar)",
 *  "紊流 k-ω SST"). Summary/badge sites still use FLOW_TYPE_LABELS for the
 *  bare technical string. */
export const FLOW_TYPE_LABEL_KEYS: Record<FlowType, ParseKeys> = {
  laminar: 'simopsSolver.form.flowTypeOptions.laminar',
  k_epsilon: 'simopsSolver.form.flowTypeOptions.k_epsilon',
  k_omega: 'simopsSolver.form.flowTypeOptions.k_omega',
  k_omega_sst: 'simopsSolver.form.flowTypeOptions.k_omega_sst',
};

/** i18n keys for the bare flow-type name in summaries and subtitles
 *  ("Laminar" / "層流", "k-ω SST") — FLOW_TYPE_LABELS rendered "Laminar" in
 *  English on every locale (ENG-3097). */
export const FLOW_TYPE_SHORT_KEYS: Record<FlowType, ParseKeys> = {
  laminar: 'simopsSolver.form.flowTypeShort.laminar',
  k_epsilon: 'simopsSolver.form.flowTypeShort.k_epsilon',
  k_omega: 'simopsSolver.form.flowTypeShort.k_omega',
  k_omega_sst: 'simopsSolver.form.flowTypeShort.k_omega_sst',
};

export const RAS_FLOW_TYPES: ReadonlySet<FlowType> = new Set([
  'k_epsilon',
  'k_omega',
  'k_omega_sst',
]);

export const ENABLED_FLOW_TYPES: ReadonlySet<FlowType> = new Set([
  'laminar',
  'k_epsilon',
  'k_omega',
  'k_omega_sst',
]);

/**
 * Per-flow-type default-value summary surfaced as a hint card under the
 * Flow Type select. Lets users see what they're getting *before* expanding
 * Advanced — non-blank entries here mean the user hasn't overridden them
 * and the solver will run with these values.
 *
 * Values mirror schema defaults in
 * models/src/khorium_models/sim_params.py — keep in sync when those
 * change. The `notesKey` line carries any flow-type-specific guidance
 * (e.g. which RAS model trades wall accuracy for freestream stability),
 * resolved at render via `t(notesKey)` so it localizes.
 *
 * Row values are usually locale-invariant literals (numbers, `1e-4 / 5e-3`),
 * but one — the length-scale hint — is prose that needs translation.
 * `[labelKey, value]` values of the literal-string shape render as-is;
 * `{ key: ParseKeys }`-shaped values resolve via `t()`.
 */
export interface FlowTypeDefaults {
  notesKey: ParseKeys;
  defaults: ReadonlyArray<
    [labelKey: ParseKeys, value: string | { key: ParseKeys }]
  >;
}

// Sub-block anchor for the FlowTypeDefaultsCard copy, so the FLOW_TYPE_DEFAULTS
// table below stays scannable at the row level. Each entry points inside
// `simopsSolver.form.flowTypeDefaultsCard`.
const CARD_LABEL: Record<string, ParseKeys> = {
  maxIterations: 'simopsSolver.form.flowTypeDefaultsCard.labels.maxIterations',
  residualPU: 'simopsSolver.form.flowTypeDefaultsCard.labels.residualPU',
  writeInterval: 'simopsSolver.form.flowTypeDefaultsCard.labels.writeInterval',
  nonOrthCorrectors:
    'simopsSolver.form.flowTypeDefaultsCard.labels.nonOrthCorrectors',
  relaxPU: 'simopsSolver.form.flowTypeDefaultsCard.labels.relaxPU',
  turbulenceIntensity:
    'simopsSolver.form.flowTypeDefaultsCard.labels.turbulenceIntensity',
  lengthScale: 'simopsSolver.form.flowTypeDefaultsCard.labels.lengthScale',
};
const LENGTH_SCALE_HINT = {
  key: 'simopsSolver.form.flowTypeDefaultsCard.values.lengthScaleHint',
} as const;

export const FLOW_TYPE_DEFAULTS: Record<FlowType, FlowTypeDefaults> = {
  laminar: {
    notesKey: 'simopsSolver.form.flowTypeDefaultsCard.notes.laminar',
    defaults: [
      [CARD_LABEL.maxIterations, '1000'],
      [CARD_LABEL.residualPU, '1e-4 / 5e-3'],
      [CARD_LABEL.writeInterval, '50'],
      [CARD_LABEL.nonOrthCorrectors, '2'],
      [CARD_LABEL.relaxPU, '0.3 / 0.5'],
    ],
  },
  k_epsilon: {
    notesKey: 'simopsSolver.form.flowTypeDefaultsCard.notes.k_epsilon',
    defaults: [
      [CARD_LABEL.turbulenceIntensity, '0.05'],
      [CARD_LABEL.lengthScale, LENGTH_SCALE_HINT],
      [CARD_LABEL.maxIterations, '2000'],
      [CARD_LABEL.residualPU, '1e-4 / 5e-3'],
      [CARD_LABEL.relaxPU, '0.2 / 0.5'],
    ],
  },
  k_omega: {
    notesKey: 'simopsSolver.form.flowTypeDefaultsCard.notes.k_omega',
    defaults: [
      [CARD_LABEL.turbulenceIntensity, '0.05'],
      [CARD_LABEL.lengthScale, LENGTH_SCALE_HINT],
      [CARD_LABEL.maxIterations, '2000'],
      [CARD_LABEL.residualPU, '1e-4 / 5e-3'],
      [CARD_LABEL.relaxPU, '0.2 / 0.5'],
    ],
  },
  k_omega_sst: {
    notesKey: 'simopsSolver.form.flowTypeDefaultsCard.notes.k_omega_sst',
    defaults: [
      [CARD_LABEL.turbulenceIntensity, '0.05'],
      [CARD_LABEL.lengthScale, LENGTH_SCALE_HINT],
      [CARD_LABEL.maxIterations, '2000'],
      [CARD_LABEL.residualPU, '1e-4 / 5e-3'],
      [CARD_LABEL.relaxPU, '0.2 / 0.5'],
    ],
  },
};

export const FLOW_AXIS_OPTIONS: readonly FlowAxis[] = ['x', 'y', 'z'];

export const FLOW_AXIS_LABELS: Record<FlowAxis, string> = {
  x: 'X',
  y: 'Y',
  z: 'Z',
};

/** Max design points before showing a warning (Lambda fanout budget). */
export const MAX_MATRIX_WARNING = 12;

/**
 * Tolerance on the asymptotic ratio (Celik et al. 2008): a value within this
 * window of 1.0 indicates the GCI grids are in the asymptotic range and the
 * extrapolated error is trustworthy. Anything further suggests the meshes
 * are too coarse to be in that range.
 */
export const GCI_ASYMPTOTIC_TOLERANCE = 0.1;

export type OpenFoamOverrideField = {
  /** i18n key for the field label (short display name like "Max Iterations"). */
  labelKey: ParseKeys;
  placeholder: string;
  integer?: boolean;
  group: 'convergence' | 'solver';
  /** i18n key for the field tooltip. Localized OpenFOAM cross-reference
   *  sentence — the tooltip is where power users cross-check against the
   *  underlying `SIMPLE.residualControl.*` / `endTime` / etc keys. */
  descriptionKey?: ParseKeys;
  /** Widget kind. Default `'number'` is the numeric OverrideInput. `'select'`
   *  renders a dropdown against `options`. `'checkbox'` is a bool toggle
   *  (`simple_consistent`, `write_compression`). `'hours'` is a duration
   *  stored in seconds and shown in hours (`max_run_time_s`, see
   *  `./time-limit`). `'hidden'` skips the panel render entirely — kept in
   *  the map so `Record<keyof SimpleAlgorithmOverrides, …>` stays exhaustive
   *  (BE-added key catches the compile) without dragging the raw-JSON
   *  `linear_solvers` shape into the panel. */
  widget?: 'number' | 'select' | 'checkbox' | 'hours' | 'hidden' | 'map';
  /** Enum options for `widget: 'select'`. Order = dropdown order. */
  options?: readonly string[];
  /** `widget: 'map'` is an equation->target dict, not a scalar: CHT's
   *  `residual_control_fluid` / `_solid`. It renders as ONE ROW PER ENTRY,
   *  labelled with the stored key, so a consumer must expand it rather than
   *  stringify it — `String({p_rgh: 1e-4})` is `[object Object]`.
   *
   *  The key is printed as stored and never translated. Since ENG-1384 a
   *  study carries the names the solver actually solves (`p_rgh`, `h`), and
   *  an older one still carries `p` / `T`; showing what the run was
   *  submitted with is the only claim this layer can support. Same rule the
   *  Verification panel's target rows follow. */
  mapLabelKey?: ParseKeys;
  /** Per-option i18n key for the SelectItem display label. Wire value is the
   *  raw option string; label is `t(optionLabelKeys[option])`. Missing entry
   *  falls back to the raw option token. */
  optionLabelKeys?: Readonly<Record<string, ParseKeys>>;
  /** Hide the field on laminar flow (relax_turbulence is RAS-only per BE
   *  docstring "Ignored on laminar"). */
  hideOnLaminar?: boolean;
  /** Skip this row on the DP methodology / report surface. Runtime /
   *  output settings (`write_format`, `write_precision`,
   *  `write_compression`) aren't physics — the reader inspecting a
   *  completed report doesn't need to see the case-file dump precision
   *  choice next to the solver knobs. Still edit-visible on the draft
   *  Advanced panel. */
  hideOnReport?: boolean;
};

// Keyed by `keyof SimpleAlgorithmOverrides` so TS errors if the backend adds a field
// and the map isn't updated. Placeholders mirror schema defaults in
// models/src/khorium_models/sim_params.py::SimpleAlgorithmOverrides.
// Declaration order determines UI render order (JS preserves insertion order
// for string keys, and `Object.keys` returns them that way).
/** RAS-specific placeholder overrides that supersede the schema defaults.
 *  Backend `_OpenFoamCFDBase._apply_flow_type_defaults` resolves `None` per
 *  flow_type at construction; the panel mirrors those defaults in the
 *  placeholder so the user sees what will land if they leave the field blank. */
export const RAS_PLACEHOLDER_OVERRIDES: Partial<
  Record<keyof SimpleAlgorithmOverrides, string>
> = {
  end_time: '2000',
  relax_p: '0.2',
  // relax_turbulence has no laminar default (BE ignores it there); RAS
  // resolves to 0.5 when the user leaves the field blank.
  relax_turbulence: '0.5',
};

export const OPENFOAM_OVERRIDE_FIELDS: Record<
  keyof SimpleAlgorithmOverrides,
  OpenFoamOverrideField
> = {
  end_time: {
    labelKey: 'simopsSolver.openFoamOverrides.endTime.label',
    placeholder: '1000',
    integer: true,
    group: 'convergence',
    descriptionKey: 'simopsSolver.openFoamOverrides.endTime.description',
  },
  // ENG-2864: the run's wall-clock limit, next to the iteration cap it races.
  // Seconds on the wire, hours on screen. Off the report: like the write
  // settings, it is how long the run was allowed, not physics.
  max_run_time_s: {
    labelKey: 'simopsSolver.openFoamOverrides.maxRunTime.label',
    placeholder: String(secondsToHours(TIME_LIMIT_DEFAULT_S)),
    group: 'convergence',
    descriptionKey: 'simopsSolver.openFoamOverrides.maxRunTime.description',
    widget: 'hours',
    hideOnReport: true,
  },
  residual_control_p: {
    labelKey: 'simopsSolver.openFoamOverrides.residualControlP.label',
    placeholder: '1e-4',
    group: 'convergence',
    descriptionKey:
      'simopsSolver.openFoamOverrides.residualControlP.description',
  },
  residual_control_u: {
    labelKey: 'simopsSolver.openFoamOverrides.residualControlU.label',
    placeholder: '5e-3',
    group: 'convergence',
    descriptionKey:
      'simopsSolver.openFoamOverrides.residualControlU.description',
  },
  write_interval: {
    labelKey: 'simopsSolver.openFoamOverrides.writeInterval.label',
    placeholder: '50',
    integer: true,
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.writeInterval.description',
  },
  n_non_orthogonal_correctors: {
    labelKey: 'simopsSolver.openFoamOverrides.nNonOrthCorrectors.label',
    placeholder: '2',
    integer: true,
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.nNonOrthCorrectors.description',
  },
  relax_p: {
    labelKey: 'simopsSolver.openFoamOverrides.relaxP.label',
    placeholder: '0.3',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.relaxP.description',
  },
  relax_u: {
    labelKey: 'simopsSolver.openFoamOverrides.relaxU.label',
    placeholder: '0.5',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.relaxU.description',
  },
  // k + dissipation (ω or ε) under-relaxation — RAS only. Hidden on laminar
  // because BE ignores it there (`Ignored on laminar` in schema docstring).
  relax_turbulence: {
    labelKey: 'simopsSolver.openFoamOverrides.relaxTurbulence.label',
    placeholder: '0.5',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.relaxTurbulence.description',
    hideOnLaminar: true,
  },
  // Gradient discretization. `unlimited` fastest + accurate on near-orthogonal
  // hex snappy; `cell_limited_*` clamp gradients on skewed tet fallbacks.
  grad_scheme: {
    labelKey: 'simopsSolver.openFoamOverrides.gradScheme.label',
    placeholder: 'unlimited',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.gradScheme.description',
    widget: 'select',
    options: ['unlimited', 'cell_limited_half', 'cell_limited_full'],
    optionLabelKeys: {
      unlimited: 'simopsSolver.openFoamOverrides.gradScheme.options.unlimited',
      cell_limited_half:
        'simopsSolver.openFoamOverrides.gradScheme.options.cell_limited_half',
      cell_limited_full:
        'simopsSolver.openFoamOverrides.gradScheme.options.cell_limited_full',
    },
  },
  // Momentum convection. `linear_upwind` 2nd-order + stable default; `upwind`
  // 1st-order monotone for marginal meshes; `limited_linear` for shocks.
  div_scheme: {
    labelKey: 'simopsSolver.openFoamOverrides.divScheme.label',
    placeholder: 'linear_upwind',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.divScheme.description',
    widget: 'select',
    options: ['upwind', 'linear_upwind', 'limited_linear', 'linear'],
    optionLabelKeys: {
      upwind: 'simopsSolver.openFoamOverrides.divScheme.options.upwind',
      linear_upwind:
        'simopsSolver.openFoamOverrides.divScheme.options.linear_upwind',
      limited_linear:
        'simopsSolver.openFoamOverrides.divScheme.options.limited_linear',
      linear: 'simopsSolver.openFoamOverrides.divScheme.options.linear',
    },
  },
  // Per-field GAMG/PBiCGStab/PCG dict. Power-user JSON shape (partial dict
  // over `p`, `U`, `turbulence`); deliberately not surfaced in the Advanced
  // panel. Kept here so the `Record<keyof SimpleAlgorithmOverrides, …>` stays
  // exhaustive — a future BE-added key breaks the compile until this map is
  // updated. Ship as raw JSON textbox in a follow-up.
  linear_solvers: {
    labelKey: 'simopsSolver.openFoamOverrides.linearSolvers.label',
    placeholder: '',
    group: 'solver',
    widget: 'hidden',
  },
  // ENG-1595 — turbulence convection scheme. `upwind` is the BE default
  // (1st-order stable); the second-order variants match Fluent / STAR-CCM+
  // defaults and are the "Match Fluent" preset target.
  div_scheme_turbulence: {
    labelKey: 'simopsSolver.openFoamOverrides.divSchemeTurbulence.label',
    placeholder: 'upwind',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.divSchemeTurbulence.description',
    widget: 'select',
    options: ['upwind', 'linear_upwind', 'limited_linear', 'linear'],
    optionLabelKeys: {
      upwind: 'simopsSolver.openFoamOverrides.divScheme.options.upwind',
      linear_upwind:
        'simopsSolver.openFoamOverrides.divScheme.options.linear_upwind',
      limited_linear:
        'simopsSolver.openFoamOverrides.divScheme.options.limited_linear',
      linear: 'simopsSolver.openFoamOverrides.divScheme.options.linear',
    },
  },
  // ENG-1595 — pressure-face interpolation. Empty string / undefined =
  // block default (linear). Explicit `linear` / `midPoint` / `cubic` emits
  // `interpolate(p) <scheme>` in fvSchemes. Null in the wire schema is the
  // same as unset here; the Select's placeholder covers the "leave BE
  // default" case.
  p_interpolation: {
    labelKey: 'simopsSolver.openFoamOverrides.pInterpolation.label',
    placeholder: 'linear',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.pInterpolation.description',
    widget: 'select',
    options: ['linear', 'midPoint', 'cubic'],
    optionLabelKeys: {
      linear: 'simopsSolver.openFoamOverrides.pInterpolation.options.linear',
      midPoint:
        'simopsSolver.openFoamOverrides.pInterpolation.options.midPoint',
      cubic: 'simopsSolver.openFoamOverrides.pInterpolation.options.cubic',
    },
  },
  // ENG-1614 — nut wall function. Log-law default; Spalding blends for
  // buffer y+; LowRe leaves the viscous sublayer to the mesh.
  nut_wall_function: {
    labelKey: 'simopsSolver.openFoamOverrides.nutWallFunction.label',
    placeholder: 'nutkWallFunction',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.nutWallFunction.description',
    widget: 'select',
    options: [
      'nutkWallFunction',
      'nutUSpaldingWallFunction',
      'nutLowReWallFunction',
    ],
    optionLabelKeys: {
      nutkWallFunction:
        'simopsSolver.openFoamOverrides.nutWallFunction.options.nutkWallFunction',
      nutUSpaldingWallFunction:
        'simopsSolver.openFoamOverrides.nutWallFunction.options.nutUSpaldingWallFunction',
      nutLowReWallFunction:
        'simopsSolver.openFoamOverrides.nutWallFunction.options.nutLowReWallFunction',
    },
  },
  // ENG-1614 — k wall function. Default handles both log-law + buffer y+.
  k_wall_function: {
    labelKey: 'simopsSolver.openFoamOverrides.kWallFunction.label',
    placeholder: 'kqRWallFunction',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.kWallFunction.description',
    widget: 'select',
    options: ['kqRWallFunction', 'kLowReWallFunction', 'fixedValue'],
    optionLabelKeys: {
      kqRWallFunction:
        'simopsSolver.openFoamOverrides.kWallFunction.options.kqRWallFunction',
      kLowReWallFunction:
        'simopsSolver.openFoamOverrides.kWallFunction.options.kLowReWallFunction',
      fixedValue:
        'simopsSolver.openFoamOverrides.kWallFunction.options.fixedValue',
    },
  },
  // ENG-1614 — omega wall function. Default is already the all-y+ blended
  // form; `fixedValue` is for prescribed BC comparisons.
  omega_wall_function: {
    labelKey: 'simopsSolver.openFoamOverrides.omegaWallFunction.label',
    placeholder: 'omegaWallFunction',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.omegaWallFunction.description',
    widget: 'select',
    options: ['omegaWallFunction', 'fixedValue'],
    optionLabelKeys: {
      omegaWallFunction:
        'simopsSolver.openFoamOverrides.omegaWallFunction.options.omegaWallFunction',
      fixedValue:
        'simopsSolver.openFoamOverrides.omegaWallFunction.options.fixedValue',
    },
  },
  // ENG-1614 — epsilon wall function. CHT-only in practice (k-epsilon
  // turbulence model); shown on the CFD panel too since the same solver
  // config surface covers both templates.
  epsilon_wall_function: {
    labelKey: 'simopsSolver.openFoamOverrides.epsilonWallFunction.label',
    placeholder: 'epsilonWallFunction',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.epsilonWallFunction.description',
    widget: 'select',
    options: ['epsilonWallFunction', 'epsilonLowReWallFunction'],
    optionLabelKeys: {
      epsilonWallFunction:
        'simopsSolver.openFoamOverrides.epsilonWallFunction.options.epsilonWallFunction',
      epsilonLowReWallFunction:
        'simopsSolver.openFoamOverrides.epsilonWallFunction.options.epsilonLowReWallFunction',
    },
  },
  // ENG-1626 — turbulence-residual convergence target. External_flow only;
  // CHT has a richer residual dict handled BE-side.
  residual_control_turbulence: {
    labelKey: 'simopsSolver.openFoamOverrides.residualControlTurbulence.label',
    placeholder: '1e-4',
    group: 'convergence',
    descriptionKey:
      'simopsSolver.openFoamOverrides.residualControlTurbulence.description',
  },
  // ENG-1626 — laplacian face-gradient limiter. 1.0 = full explicit
  // correction (matches Fluent / STAR-CCM+); default 0.33 damps for
  // stability on skewed meshes.
  laplacian_limiter: {
    labelKey: 'simopsSolver.openFoamOverrides.laplacianLimiter.label',
    placeholder: '0.33',
    group: 'convergence',
    descriptionKey:
      'simopsSolver.openFoamOverrides.laplacianLimiter.description',
  },
  // ENG-1626 — SIMPLE.consistent yes/no. false matches Fluent / STAR-CCM+
  // defaults. SimpleAlgorithmOverrides only (not on ChtOverrides).
  simple_consistent: {
    labelKey: 'simopsSolver.openFoamOverrides.simpleConsistent.label',
    placeholder: 'true',
    group: 'convergence',
    descriptionKey:
      'simopsSolver.openFoamOverrides.simpleConsistent.description',
    widget: 'checkbox',
  },
  // ENG-1627 — pRefCell (closed-domain pressure reference cell index).
  pref_cell: {
    labelKey: 'simopsSolver.openFoamOverrides.prefCell.label',
    placeholder: '0',
    integer: true,
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.prefCell.description',
  },
  // ENG-1627 — pRefValue (pressure pinned at pref_cell).
  pref_value: {
    labelKey: 'simopsSolver.openFoamOverrides.prefValue.label',
    placeholder: '0.0',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.prefValue.description',
  },
  // ENG-1627 — controlDict write format. Binary ≈ 10× smaller time-step
  // directories at multi-M cells.
  write_format: {
    labelKey: 'simopsSolver.openFoamOverrides.writeFormat.label',
    placeholder: 'ascii',
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.writeFormat.description',
    widget: 'select',
    options: ['ascii', 'binary'],
    optionLabelKeys: {
      ascii: 'simopsSolver.openFoamOverrides.writeFormat.options.ascii',
      binary: 'simopsSolver.openFoamOverrides.writeFormat.options.binary',
    },
    hideOnReport: true,
  },
  // ENG-1627 — controlDict write precision. 6 default; 15 for Cd/Cl parity
  // comparisons against other solvers.
  write_precision: {
    labelKey: 'simopsSolver.openFoamOverrides.writePrecision.label',
    placeholder: '6',
    integer: true,
    group: 'solver',
    descriptionKey: 'simopsSolver.openFoamOverrides.writePrecision.description',
    hideOnReport: true,
  },
  // ENG-1627 — controlDict zlib on/off. Renders as `on`/`off` in the case
  // file, not Python True/False.
  write_compression: {
    labelKey: 'simopsSolver.openFoamOverrides.writeCompression.label',
    placeholder: 'false',
    group: 'solver',
    descriptionKey:
      'simopsSolver.openFoamOverrides.writeCompression.description',
    widget: 'checkbox',
    hideOnReport: true,
  },
};

/**
 * A set override as the read-only solver summaries print it: an option's
 * label rather than its OpenFOAM literal, and a time limit in hours, since it
 * is stored in seconds (ENG-2865).
 */
export function formatOverrideValue(
  field: OpenFoamOverrideField,
  value: unknown,
  t: TFunction,
): string {
  if (field.widget === 'hours' && typeof value === 'number')
    return String(secondsToHours(value));
  const optionKey = field.optionLabelKeys?.[String(value)];
  return optionKey ? t(optionKey) : String(value);
}

export function getOverridePlaceholder(
  key: string,
  isRas: boolean,
  template?: string | null,
): string {
  const rasDefault =
    RAS_PLACEHOLDER_OVERRIDES[key as keyof SimpleAlgorithmOverrides];
  return (
    (isRas && rasDefault) || overrideFieldsFor(template)[key]?.placeholder || ''
  );
}

/**
 * The CHT knob set, in render order.
 *
 * `chtMultiRegionSimpleFoam` is not `simpleFoam` with extra bits: the models
 * share some controls and each has its own. Until ENG-2523 every surface read the
 * CFD table for both, which had two consequences on a CHT study: the ten
 * CHT-only knobs were unrenderable — `relax_h` and BOTH residual-target maps
 * among them, so a user could set a target, save it, and never see it again —
 * and the six CFD-only ones could render, quoting a `Residual p` default on a
 * field `chtMultiRegionSimpleFoam` does not solve under that name. That second
 * one is the defect ENG-1384 removed from the case writer, one layer up.
 *
 * Shared descriptors are referenced, not copied: the label and description
 * text is identical line-for-line and a second copy would drift. Only the key
 * order and the CHT-only entries live here.
 *
 * `Record<keyof ChtOverrides, …>` is the point of the shape — a field added to
 * the backend model now fails the frontend build instead of silently never
 * appearing, which is how these ten went unnoticed.
 */
export const CHT_OVERRIDE_FIELDS: Record<
  keyof ChtOverrides,
  OpenFoamOverrideField
> = {
  end_time: OPENFOAM_OVERRIDE_FIELDS.end_time,
  max_run_time_s: OPENFOAM_OVERRIDE_FIELDS.max_run_time_s,
  // The two dict-valued ones, next to the iteration cap they race against:
  // the run stops at whichever comes first.
  residual_control_fluid: {
    labelKey: 'simopsSolver.cht.residualFluid',
    mapLabelKey: 'simopsSolver.cht.residualFluid',
    // No placeholder, and deliberately not the model-side default: the
    // backend field is a whole dict with a `default_factory`, so a study
    // that sent `{U: 1e-6}` controlled ONE equation and quoting the rest
    // would name bars the run was never given. Absent bag = no rows.
    placeholder: '',
    group: 'convergence',
    widget: 'map',
    descriptionKey: 'simopsSolver.cht.residualFluidInfo',
  },
  residual_control_solid: {
    labelKey: 'simopsSolver.cht.residualSolid',
    mapLabelKey: 'simopsSolver.cht.residualSolid',
    // No placeholder, and deliberately not the model-side default: the
    // backend field is a whole dict with a `default_factory`, so a study
    // that sent `{U: 1e-6}` controlled ONE equation and quoting the rest
    // would name bars the run was never given. Absent bag = no rows.
    placeholder: '',
    group: 'convergence',
    widget: 'map',
    descriptionKey: 'simopsSolver.cht.residualSolidInfo',
  },
  laplacian_limiter: OPENFOAM_OVERRIDE_FIELDS.laplacian_limiter,
  // ENG-3109: the solids' own limiter, beside the shared one it split from.
  laplacian_limiter_solid: {
    labelKey: 'simopsSolver.cht.laplacianLimiterSolid',
    placeholder: '1',
    group: 'convergence',
    descriptionKey: 'simopsSolver.cht.laplacianLimiterSolidInfo',
  },
  // Bounding clamps. CHT-only because they guard the coupled energy solve —
  // a solid runaway shows up as temperature, not as velocity.
  limit_velocity_factor: {
    labelKey: 'simopsSolver.cht.limitVelocityFactor',
    placeholder: '10',
    group: 'convergence',
    descriptionKey: 'simopsSolver.cht.limitVelocityFactorInfo',
  },
  limit_temperature_min: {
    labelKey: 'simopsSolver.cht.limitTemperatureMin',
    // Empty is meaningful and is NOT "off": the case writer derives a band
    // around the inlet temperature. Showing a number here would claim a
    // fixed bound the run does not have.
    placeholder: '',
    group: 'convergence',
    descriptionKey: 'simopsSolver.cht.limitTemperatureMinInfo',
  },
  limit_temperature_max: {
    labelKey: 'simopsSolver.cht.limitTemperatureMax',
    // Empty is meaningful and is NOT "off": the case writer derives a band
    // around the inlet temperature. Showing a number here would claim a
    // fixed bound the run does not have.
    placeholder: '',
    group: 'convergence',
    descriptionKey: 'simopsSolver.cht.limitTemperatureMaxInfo',
  },
  write_interval: OPENFOAM_OVERRIDE_FIELDS.write_interval,
  // Split per region because the two solves have different mesh quality:
  // the fluid side carries the boundary layer, the solids are usually clean.
  n_non_orthogonal_correctors_fluid: {
    labelKey: 'simopsSolver.cht.nonOrthCorrectorsFluid',
    placeholder: '2',
    integer: true,
    group: 'solver',
    descriptionKey: 'simopsSolver.cht.nonOrthCorrectorsFluidInfo',
  },
  n_non_orthogonal_correctors_solid: {
    labelKey: 'simopsSolver.cht.nonOrthCorrectorsSolid',
    placeholder: '2',
    integer: true,
    group: 'solver',
    descriptionKey: 'simopsSolver.cht.nonOrthCorrectorsSolidInfo',
  },
  relax_p: OPENFOAM_OVERRIDE_FIELDS.relax_p,
  relax_u: OPENFOAM_OVERRIDE_FIELDS.relax_u,
  // Beside its siblings rather than at the end: an operator reading the
  // relaxation block is comparing the three against each other.
  relax_h: {
    labelKey: 'simopsSolver.cht.relaxH',
    placeholder: '0.5',
    group: 'solver',
    descriptionKey: 'simopsSolver.cht.relaxHInfo',
  },
  relax_h_solid: {
    labelKey: 'simopsSolver.cht.relaxHSolid',
    placeholder: '',
    group: 'solver',
    descriptionKey: 'simopsSolver.cht.relaxHSolidInfo',
  },
  relax_turbulence: OPENFOAM_OVERRIDE_FIELDS.relax_turbulence,
  grad_scheme: OPENFOAM_OVERRIDE_FIELDS.grad_scheme,
  div_scheme: OPENFOAM_OVERRIDE_FIELDS.div_scheme,
  div_scheme_turbulence: OPENFOAM_OVERRIDE_FIELDS.div_scheme_turbulence,
  p_interpolation: OPENFOAM_OVERRIDE_FIELDS.p_interpolation,
  nut_wall_function: OPENFOAM_OVERRIDE_FIELDS.nut_wall_function,
  k_wall_function: OPENFOAM_OVERRIDE_FIELDS.k_wall_function,
  omega_wall_function: OPENFOAM_OVERRIDE_FIELDS.omega_wall_function,
  epsilon_wall_function: OPENFOAM_OVERRIDE_FIELDS.epsilon_wall_function,
  // Hidden for the same reason as the CFD `linear_solvers`: raw-JSON
  // power-user slots with no UI. Present so the Record stays exhaustive.
  linear_solvers_fluid: { ...OPENFOAM_OVERRIDE_FIELDS.linear_solvers },
  linear_solvers_solid: { ...OPENFOAM_OVERRIDE_FIELDS.linear_solvers },
  pref_cell: OPENFOAM_OVERRIDE_FIELDS.pref_cell,
  pref_value: OPENFOAM_OVERRIDE_FIELDS.pref_value,
  write_format: OPENFOAM_OVERRIDE_FIELDS.write_format,
  write_precision: OPENFOAM_OVERRIDE_FIELDS.write_precision,
  write_compression: OPENFOAM_OVERRIDE_FIELDS.write_compression,
};

// ENG-1595 / 1614 / 1626 / 1627. The twelve extra CHT-side knobs the case
// writer exposes on ChtOverrides, in the order the CHT panel renders them.
// They reuse the OPENFOAM_OVERRIDE_FIELDS metadata (same labels,
// descriptions, enum options) even though the key set is keyed against
// `ChtOverrides` — the field semantics match line-for-line with the
// SimpleAlgorithmOverrides side, so a second registry would be pure
// duplication. Exported for the assistant's SimOps reference sync test.
export const CHT_EXTRA_OVERRIDE_KEYS: ReadonlyArray<
  Extract<
    keyof ChtOverrides,
    | 'div_scheme_turbulence'
    | 'p_interpolation'
    | 'nut_wall_function'
    | 'k_wall_function'
    | 'omega_wall_function'
    | 'epsilon_wall_function'
    | 'laplacian_limiter'
    | 'pref_cell'
    | 'pref_value'
    | 'write_format'
    | 'write_precision'
    | 'write_compression'
  >
> = [
  'div_scheme_turbulence',
  'p_interpolation',
  'nut_wall_function',
  'k_wall_function',
  'omega_wall_function',
  'epsilon_wall_function',
  'laplacian_limiter',
  'pref_cell',
  'pref_value',
  'write_format',
  'write_precision',
  'write_compression',
];

/** The descriptor table for a template. Anything not CHT is CFD-shaped —
 *  `external_flow`, `internal_flow` and `urans` all speak
 *  `SimpleAlgorithmOverrides`. */
export function overrideFieldsFor(
  template: string | null | undefined,
): Record<string, OpenFoamOverrideField> {
  return template === 'cht_steady'
    ? CHT_OVERRIDE_FIELDS
    : OPENFOAM_OVERRIDE_FIELDS;
}

/**
 * Ordered keys the Advanced-panel AND SolverConfigSummary should render for a
 * given template + group + flow-type. Filters both `widget: 'hidden'` fields
 * (the power-user JSON solver slots, no UI) and any `hideOnLaminar` field on a
 * laminar study (currently `relax_turbulence` — BE ignores it there). Shared
 * so the edit surface and the read-only summary can never disagree — e.g.
 * render a "Linear Solvers: [object Object]" row or a "Relax Turbulence" row
 * that the solver silently drops.
 *
 * `template` selects the knob set (ENG-2523). Omitting it keeps the CFD table,
 * which is what every caller outside the CHT surfaces wants; passing
 * `'cht_steady'` is what stops a CHT study being described with `simpleFoam`'s
 * vocabulary.
 */
export function visibleOverrideFields(
  group: 'convergence' | 'solver',
  opts: { isRas: boolean; template?: string | null },
): string[] {
  const fields = overrideFieldsFor(opts.template);
  return Object.keys(fields).filter((k) => {
    const f = fields[k];
    return (
      f.group === group &&
      f.widget !== 'hidden' &&
      !(f.hideOnLaminar && !opts.isRas)
    );
  });
}

// Report composer thresholds. Used by report-content.ts. Bumping any of
// these changes which caveats / drop-list bullets appear in the rendered
// study report.
export const REPORT_R2_LOW = 0.8;
export const REPORT_COVERAGE_LOW = 0.9;
export const REPORT_RESIDUAL_CORR_HIGH = 0.4;
export const REPORT_SOBOL_INERT = 0.05;
export const REPORT_SOBOL_INTERACTION = 0.15;
export const REPORT_TOP_K = 5;
export const REPORT_ERROR_MSG_FINGERPRINT_LEN = 60;

export const DP_DETAIL_TABS = [
  'inputs',
  'residuals',
  'outputs',
  'visualization',
  'phases',
  'files',
] as const;
export type DpDetailTab = (typeof DP_DETAIL_TABS)[number];
